import { z } from 'zod';
import { Email, IsoDateTime, Phone, Slug, Uuid } from './common.js';
/**
 * Public pre-registration forms (spec 8.5, 7 `forms`) and the Outlook add-in import (spec 8.5). Part of module
 * `pre-registration` (docs/modules/pre-registration-phase4.md). Owners: S45b (forms), S45 (Outlook import).
 * Permissions: forms.view / forms.manage / forms.delete. Public pages live on the link host at /<tenantSlug>/forms/<slug>.
 */
export const FormHostMode = z.enum(['none', 'optional', 'required']);
export const PublicFormFields = z.object({
    email: z.enum(['optional', 'required']).default('required'),
    phone: z.enum(['off', 'optional', 'required']).default('optional'),
    company: z.enum(['off', 'optional', 'required']).default('optional'),
    /** The visitor types the host's name; the server matches an exact unique name (never shows a staff list publicly). */
    host: FormHostMode.default('optional'),
    /** Sign-in questions of the group asked in advance (ids of the group's sign-in fields). Sensitive fields are refused. */
    fieldIds: z.array(Uuid).max(30).default([]),
});
export const PublicForm = z.object({
    id: Uuid,
    name: z.string(),
    slug: Slug,
    siteId: Uuid,
    groupId: Uuid,
    enabled: z.boolean(),
    fields: PublicFormFields,
    intro: z.string(),
    thanks: z.string(),
    /** Submissions wait for approval (today.approve_preregistrations). On by default. */
    requireApproval: z.boolean(),
    /** How far ahead a visit can be booked. */
    maxDaysAhead: z.number().int(),
    consentText: z.string(),
    submissions30d: z.number().int(),
    publicUrl: z.string(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const PublicFormCreate = z.object({
    name: z.string().trim().min(1).max(120),
    slug: Slug,
    siteId: Uuid,
    groupId: Uuid,
    enabled: z.boolean().default(true),
    fields: PublicFormFields.default(PublicFormFields.parse({})),
    intro: z.string().max(4000).default(''),
    thanks: z.string().max(2000).default(''),
    requireApproval: z.boolean().default(true),
    maxDaysAhead: z.number().int().min(1).max(365).default(90),
    consentText: z.string().max(2000).default(''),
});
export const PublicFormUpdate = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    slug: Slug.optional(),
    siteId: Uuid.optional(),
    groupId: Uuid.optional(),
    enabled: z.boolean().optional(),
    fields: PublicFormFields.optional(),
    intro: z.string().max(4000).optional(),
    thanks: z.string().max(2000).optional(),
    requireApproval: z.boolean().optional(),
    maxDaysAhead: z.number().int().min(1).max(365).optional(),
    consentText: z.string().max(2000).optional(),
});
/** GET /pre-registration/public/forms/:tenantSlug/:slug (public): what the page needs. 404 when off or unknown. */
export const PublicFormView = z.object({
    tenantName: z.string(),
    siteName: z.string(),
    siteTimeZone: z.string(),
    groupName: z.string(),
    intro: z.string(),
    consentText: z.string(),
    privacyUrl: z.string().nullable(),
    fields: PublicFormFields.omit({ fieldIds: true }),
    questions: z.array(z.object({ id: Uuid, label: z.string(), type: z.string(), required: z.boolean(), options: z.array(z.string()) })),
    maxDaysAhead: z.number().int(),
});
export const PublicFormSubmit = z.object({
    name: z.string().trim().min(1).max(120),
    email: Email.nullable().default(null),
    phone: Phone.nullable().default(null),
    company: z.string().trim().max(120).nullable().default(null),
    hostName: z.string().trim().max(120).nullable().default(null),
    expectedAt: IsoDateTime,
    answers: z.record(z.string(), z.unknown()).default({}),
    consent: z.boolean().default(false),
    /** Honeypot: must be empty. */
    website: z.string().max(0).default(''),
});
export const PublicFormSubmitResult = z.object({ status: z.enum(['pending', 'approved']), thanks: z.string() });
// --- Outlook add-in (S45) --------------------------------------------------------------------------------------------------------
/**
 * POST /pre-registration/outlook/meeting (user token purpose 'outlook', or a portal session). Re-sending the same
 * `itemKey` updates the meeting's pre-registrations (new guests added, removed guests cancelled, times moved).
 * Internal attendees (emails of current members in a non-visitor group, or the tenant's own domains) are skipped.
 */
export const OutlookMeetingImport = z.object({
    /** Hash of the Exchange item id made by the add-in (SHA-256 hex), so the item id itself is never stored. */
    itemKey: z.string().regex(/^[0-9a-f]{64}$/),
    subject: z.string().max(255).default(''),
    start: IsoDateTime,
    end: IsoDateTime,
    siteId: Uuid,
    groupId: Uuid,
    /** Defaults to the member whose email is the signed-in user's. */
    hostMemberId: Uuid.nullable().default(null),
    attendees: z.array(z.object({ name: z.string().trim().max(120).default(''), email: Email })).max(50),
    sendEmail: z.boolean().default(true),
});
export const OutlookMeetingResult = z.object({
    created: z.number().int(),
    updated: z.number().int(),
    cancelled: z.number().int(),
    skipped: z.array(z.object({ email: z.string(), reasonKey: z.string() })),
    preRegistrationIds: z.array(Uuid),
});
export const OutlookMeetingCancel = z.object({ itemKey: z.string().regex(/^[0-9a-f]{64}$/) });
/** GET /pre-registration/outlook/context: sites and groups the user may pre-register into, and their own member id. */
export const OutlookContext = z.object({
    sites: z.array(z.object({ id: Uuid, name: z.string() })),
    groups: z.array(z.object({ id: Uuid, name: z.string(), siteIds: z.array(Uuid) })),
    hostMemberId: Uuid.nullable(),
    tenantName: z.string(),
});
//# sourceMappingURL=forms.js.map